import React, { useContext, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import CourseCardDashboard from "../components/CourseCardDashboard";
import { NameContext } from "../context/NameContext";
import UpdateUserProfileModal from "../components/UpdateUserProfileModal";
import PasswordChangeModal from "./PasswordChangeModal";
import PaperBG from "../kits/DesignKit/PaperBG";
import MonoButton from "../kits/DesignKit/MonoButton";
import ZenEditorDemo from "./ZenEditorDemo";
import LMSDemo from "./LMSDemo";
import BookingModal from "./BookingModal";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from "react-router-dom"; // <-- useSearchParams hinzufügen
import ContactForm from "./ContactForm";

const Dashboard = () => {
  const { userId, logout, token, mustChangePassword, setMustChangePassword } = useAuth();
  const [courses, setCourses] = useState([]);
  const [error, setError] = useState("");
  const [firstName, setFirstName] = useState("");
  const { setName, setIsNameEntered } = useContext(NameContext);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const demoMode = searchParams.get("demo"); // Holt den demo Parameter aus der URL
  const [showBooking, setShowBooking] = useState(false); // Booking Modal State

  // Scroll to center modal in viewport when booking modal opens
  useEffect(() => {
    if (showBooking) {
      // Calculate scroll position to center modal vertically in viewport
      const viewportHeight = window.innerHeight;
      const scrollTop = window.scrollY;
      const documentHeight = document.documentElement.scrollHeight;

      // Scroll to a position that leaves the modal centered
      // Assuming modal is ~400-500px tall, we want it centered in viewport
      const targetScroll = Math.max(0, scrollTop - (viewportHeight / 4));

      window.scrollTo({ top: targetScroll, behavior: 'smooth' });
    }
  }, [showBooking]);

  useEffect(() => {
    // Wenn Demo-Modus aktiv ist, kein Login erforderlich
    if (demoMode) {
      return;
    }

    if (!userId) {
      navigate("/login"); // Sofort zurück zum Login wenn keine Auth!
      return;
    }

    const fetchCourses = async () => {
      try {
        const res = await fetch(
          `https://www.denisbitter.de/stage01/api/get_user_courses.php?userId=${userId}`
        );
        const data = await res.json();

        if (data.success && Array.isArray(data.data)) {
          const rawCourses = data.data;
          const normalizedCourses = rawCourses.map((c) => ({
            id: c.course_id,
            slug: c.course_slug,
            title: c.course_title,
            description: c.course_description,
            iconName: c.course_icon,
            category: c.course_category,
            startDate: c.course_startDate,
            customDate: c.registration_custom_date,
            pdfPath: c.registration_pdf_path,
            firstName: c.registrant_first_name,
            lastName: c.registrant_last_name,
            registrationDate: c.registration_date,
            registrationId: c.registration_id,
          }));

          setCourses(normalizedCourses);

          const name = normalizedCourses[0]?.firstName || "";
          if (name) {
            setName(name);
            setIsNameEntered(true);
          }
          setFirstName(name);
        } else {
          setError(data.message || "Keine Kurse gefunden.");
        }
      } catch (err) {
        console.error("❌ Fehler beim Laden:", err);
        setError("Fehler beim Laden der Kurse.");
      }
    };

    fetchCourses();
  }, [userId, setName, setIsNameEntered, navigate, demoMode]); // demoMode als Dependency hinzufügen

  // Wenn Demo-Modus aktiv ist, zeige die entsprechende Demo-Komponente
  if (demoMode === "zeneditor") {
    return <ZenEditorDemo />;
  }

  if (demoMode === "lms") {
    return <LMSDemo />;
  }

  // Standard Dashboard für registrierte User
  return (
    <PaperBG>
      <>
        {mustChangePassword && (
          <PasswordChangeModal onClose={() => setMustChangePassword(false)} />
        )}
        <div className="min-h-screen justify-center">
          <div className="max-w-3xl mx-auto text-left px-6 py-12 font-mono">
            <h1 className="text-sm font-mono mb-1">
              Moin, {firstName ? firstName : `Benutzer #${userId}`}
              <h2 className="text-xs font-mono mb-4">Deine Trainings</h2>
            </h1>
            <div className="text-right mb-10">
              <UpdateUserProfileModal initialData={courses[0]} />
              {error && <p className="text-red-500">{error}</p>}
              <div className="justify-center py-10">
                {courses.length > 0 ? (
                  courses.map((course) => (
                    <CourseCardDashboard
                      key={course.id}
                      course={course}
                      formattedDate={course.formattedDate}
                    />
                  ))
                ) : (
                  !error && <p>Keine Kurse verfügbar.</p>
                )}
              </div>
            </div>

            {/* Demo-Bereich */}
            <div className="mt-16 border-t border-brand pt-10">
              <h2 className="text-sm font-mono mb-6">
                Entdecke meine ZenDev <span className="bg-gray-950 text-white px-2 py-1">Demos. Made by Denis Bitter</span>
              </h2>
              <div className="mt-10 border-t border-brand pt-10"></div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* ZenEditor Demo Card */}
                <div className="bg-white border border-gray-300 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-brand/10 rounded flex items-center justify-center">
                      <span className="text-brand text-lg">✍️</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm">ZenEditor</h3>
                      <span className="text-xs text-gray-600">Hybrid Content Editor</span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-700 mb-4">
                    Kombiniere EditorJS mit Markdown-Support. Hybrid, flexibel und portabel.
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="text-brand">✓</span> Markdown & EditorJS
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="text-brand">✓</span> Live Preview
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="text-brand">✓</span> Export & Import
                    </div>
                  </div>

                  <MonoButton
                    onClick={() => navigate("/dashboard?demo=zeneditor")}
                    className="w-full text-xs py-2"
                  >
                    Demo jetzt Online 
                  </MonoButton>
                </div>

                {/* LMS System Demo Card */}
                <div className="bg-white border border-gray-300 rounded-lg p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-brand/10 rounded flex items-center justify-center">
<span className="text-brand text-lg">
  <FontAwesomeIcon icon={faCalendarDays} style={{ fontSize: '10px', marginTop: '-2px' }} />
</span>                    </div>
                    <div>
                      <h3 className="font-semibold text-sm">LMS System</h3>
                      <span className="text-xs text-gray-600">Learning Management</span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-700 mb-4">
                    Kurs-Editor, User-Management, Progress-Tracking und Zertifikate in einem System.
                  </p>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="text-brand">✓</span> Kurs-Management
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="text-brand">✓</span> Progress-Tracking
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="text-brand">✓</span> User-Dashboard
                    </div>
                  </div>

                  <MonoButton
                    onClick={() => navigate("/dashboard?demo=lms")}
                    className="w-full text-xs py-2"
                  >
                    Demo Online ausprobieren
                  </MonoButton>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-xs text-gray-600">
                  Interesse an einem dieser Systeme?{" "}
                  <button
                    onClick={() => setShowBooking(true)}
                    className="text-brand underline hover:no-underline cursor-pointer"
                  >
                    Kontaktiere mich per eMail
                  </button>
                </p>
              </div>
              <ContactForm />
            </div>
          </div>
        </div>

        {/* Booking Modal */}
        {showBooking && <BookingModal onClose={() => setShowBooking(false)} />}
      </>
    </PaperBG>
  );
};

export default Dashboard;
